import React from 'react';
import { XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline';

interface Coordinates {
  lat: number;
  lng: number;
}

interface RouteMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  origin: Coordinates;
  destination: Coordinates;
  originAddress?: string;
  destinationAddress?: string;
}

const RouteMapModal: React.FC<RouteMapModalProps> = ({
  isOpen,
  onClose,
  origin,
  destination,
  originAddress,
  destinationAddress,
}) => {
  if (!isOpen) return null;

  // Generate Google Maps directions URL
  const generateMapsUrl = () => {
    const params = new URLSearchParams({
      api: '1',
      origin: `${origin.lat},${origin.lng}`,
      destination: `${destination.lat},${destination.lng}`,
      travelmode: 'driving',
    });

    // Add addresses if available for better display
    if (originAddress) {
      params.set('origin', originAddress);
    }
    if (destinationAddress) {
      params.set('destination', destinationAddress);
    }

    return `https://www.google.com/maps/dir/?${params.toString()}`;
  };

  const openInGoogleMaps = () => {
    window.open(generateMapsUrl(), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Route Information</h3>
              <div className="mt-1 text-sm text-gray-500">
                View the route from pickup to destination
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Route Details */}
          <div className="p-6">
            <div className="space-y-6">
              {/* Origin */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
                    <MapPinIcon className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Pickup Location</h4>
                  <p className="text-sm text-gray-600 mt-1">{originAddress || 'Pickup Location'}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {origin.lat.toFixed(6)}, {origin.lng.toFixed(6)}
                  </p>
                </div>
              </div>

              {/* Destination */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
                    <MapPinIcon className="h-5 w-5 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Destination Location</h4>
                  <p className="text-sm text-gray-600 mt-1">{destinationAddress || 'Destination'}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {destination.lat.toFixed(6)}, {destination.lng.toFixed(6)}
                  </p>
                </div>
              </div>

              {/* Map Preview Placeholder */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Route Map Preview</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Click the button below to view the complete route in Google Maps
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Close
              </button>
              <button
                onClick={openInGoogleMaps}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Open in Google Maps
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteMapModal;